import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';
import { translations, projects, heroImages } from '../data/mock';
import { Moon, Sun, Menu, X, Mail, Instagram, Calendar, MapPin, ExternalLink } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import GalleryLightbox from './GalleryLightbox';

const HomePage = () => {
  const { language, changeLanguage } = useLanguage();
  const { theme, toggleTheme } = useTheme();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [selectedGallery, setSelectedGallery] = useState(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [events, setEvents] = useState([]);
  const t = translations[language];

  // Carga dinámica de eventos desde eventos.json
  useEffect(() => {
    fetch('/eventos.json')
      .then((res) => res.json())
      .then((data) => setEvents(data))
      .catch((err) => console.error('Error loading eventos.json:', err));
  }, []);

  const languages = [
    { code: 'es', label: 'ES' },
    { code: 'en', label: 'EN' },
    { code: 'fr', label: 'FR' }
  ];

  // Auto-rotate hero background images
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => 
        prevIndex === heroImages.length - 1 ? 0 : prevIndex + 1
      );
    }, 5000); // Change image every 5 seconds

    return () => clearInterval(interval);
  }, []);

  const openGallery = (project, imageIndex = 0) => {
    if (project.gallery && project.gallery.length > 0) {
      setSelectedGallery(project.gallery);
      setSelectedImageIndex(imageIndex);
      setLightboxOpen(true);
    }
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    setSelectedGallery(null);
    setSelectedImageIndex(0);
  };

  return (
    <div className="min-h-screen bg-white dark:bg-zinc-950 text-zinc-900 dark:text-zinc-100 transition-colors duration-500">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 dark:bg-zinc-950/80 backdrop-blur-md border-b border-zinc-200 dark:border-zinc-800 transition-colors duration-500">
        <nav className="container mx-auto px-6 py-4 flex items-center justify-between">
          {/* Logo */}
          <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="text-2xl font-light tracking-wider hover:opacity-70 transition-opacity" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            YM
          </button>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            <button onClick={() => document.getElementById('work')?.scrollIntoView({ behavior: 'smooth' })} className="text-sm font-light hover:opacity-70 transition-opacity">
              {t.nav.work}
            </button>
            <button onClick={() => document.getElementById('about')?.scrollIntoView({ behavior: 'smooth' })} className="text-sm font-light hover:opacity-70 transition-opacity">
              {t.nav.about}
            </button>
            <button onClick={() => document.getElementById('exhibitions')?.scrollIntoView({ behavior: 'smooth' })} className="text-sm font-light hover:opacity-70 transition-opacity">
              {t.nav.exhibitions}
            </button>
            <button onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })} className="text-sm font-light hover:opacity-70 transition-opacity">
              {t.nav.contact}
            </button>
          </div>

          {/* Controls */}
          <div className="flex items-center space-x-4">
            {/* Language Selector */}
            <div className="hidden md:flex items-center space-x-2">
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => changeLanguage(lang.code)}
                  className={`text-xs font-light px-2 py-1 rounded transition-all ${
                    language === lang.code
                      ? 'bg-zinc-900 dark:bg-zinc-100 text-zinc-100 dark:text-zinc-900'
                      : 'hover:opacity-70'
                  }`}
                >
                  {lang.label}
                </button>
              ))}
            </div>

            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              className="p-2 rounded-full hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
              aria-label="Toggle theme"
            >
              {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>

            {/* Mobile Menu Toggle */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </nav>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white dark:bg-zinc-950 border-t border-zinc-200 dark:border-zinc-800 transition-colors duration-500">
            <div className="container mx-auto px-6 py-6 space-y-4">
              <button
                className="block text-sm font-light hover:opacity-70 transition-opacity w-full text-left"
                onClick={() => {
                  document.getElementById('work')?.scrollIntoView({ behavior: 'smooth' });
                  setMobileMenuOpen(false);
                }}
              >
                {t.nav.work}
              </button>
              <button
                className="block text-sm font-light hover:opacity-70 transition-opacity w-full text-left"
                onClick={() => {
                  document.getElementById('about')?.scrollIntoView({ behavior: 'smooth' });
                  setMobileMenuOpen(false);
                }}
              >
                {t.nav.about}
              </button>
              <button
                className="block text-sm font-light hover:opacity-70 transition-opacity w-full text-left"
                onClick={() => {
                  document.getElementById('exhibitions')?.scrollIntoView({ behavior: 'smooth' });
                  setMobileMenuOpen(false);
                }}
              >
                {t.nav.exhibitions}
              </button>
              <button
                className="block text-sm font-light hover:opacity-70 transition-opacity w-full text-left"
                onClick={() => {
                  document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
                  setMobileMenuOpen(false);
                }}
              >
                {t.nav.contact}
              </button>
              <div className="flex items-center space-x-2 pt-4 border-t border-zinc-200 dark:border-zinc-800">
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => {
                      changeLanguage(lang.code);
                      setMobileMenuOpen(false);
                    }}
                    className={`text-xs font-light px-3 py-1 rounded transition-all ${
                      language === lang.code
                        ? 'bg-zinc-900 dark:bg-zinc-100 text-zinc-100 dark:text-zinc-900'
                        : 'hover:opacity-70'
                    }`}
                  >
                    {lang.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Hero Section with Image Slideshow */}
      <section className="relative h-screen flex items-center justify-center pt-16">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-white dark:to-zinc-950 z-10"></div>
          {/* Background image slideshow */}
          {heroImages.map((image, index) => (
            <img
              key={index}
              src={image}
              alt={`Hero ${index + 1}`}
              className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${
                index === currentImageIndex ? 'opacity-60 dark:opacity-40' : 'opacity-0'
              }`}
            />
          ))}
        </div>
        <div className="relative z-20 container mx-auto px-6 text-center">
          <h1 className="text-6xl md:text-8xl font-light mb-6 tracking-wide" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 300 }}>
            {t.hero.name}
          </h1>
          <p className="text-lg md:text-xl font-light mb-3 opacity-80" style={{ fontFamily: 'Raleway, sans-serif', fontWeight: 300 }}>
            {t.hero.title}
          </p>
          <p className="text-sm md:text-base font-light mb-8 opacity-60" style={{ fontFamily: 'Raleway, sans-serif', fontWeight: 300 }}>
            {t.hero.subtitle}
          </p>
          <p className="max-w-2xl mx-auto text-sm md:text-base font-light leading-relaxed opacity-70" style={{ fontFamily: 'Raleway, sans-serif', fontWeight: 300 }}>
            {t.hero.description}
          </p>
        </div>
      </section>

      {/* Work Section - Galleries */}
      <section id="work" className="py-24 bg-zinc-50 dark:bg-zinc-900 transition-colors duration-500">
        <div className="container mx-auto px-6">
          <h2 className="text-4xl md:text-5xl font-light mb-16 text-center tracking-wide" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 300 }}>
            {t.work.title}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16">
            {projects.map((project, index) => (
              <div
                key={project.id}
                className="group cursor-pointer"
                onClick={() => openGallery(project)}
              >
                <div className="relative overflow-hidden bg-zinc-200 dark:bg-zinc-800 aspect-[4/3] mb-6">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-zinc-900/0 group-hover:bg-zinc-900/20 transition-colors duration-500"></div>
                  {project.gallery && project.gallery.length > 0 && (
                    <div className="absolute bottom-4 right-4 bg-black/70 text-white px-3 py-1 rounded text-xs font-light" style={{ fontFamily: 'Raleway, sans-serif', fontWeight: 300 }}>
                      {project.gallery.length} {language === 'es' ? 'fotos' : language === 'en' ? 'photos' : 'photos'}
                    </div>
                  )}
                </div>
                <div className="space-y-3">
                  <h3 className="text-2xl md:text-3xl font-light tracking-wide" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 400 }}>
                    {project.title}
                  </h3>
                  <p className="text-sm font-light leading-relaxed opacity-70" style={{ fontFamily: 'Raleway, sans-serif', fontWeight: 300 }}>
                    {project.description[language]}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* <!-- INICIO SECCIÓN EVENTOS --> */}
      <section id="exhibitions" className="py-24 bg-white dark:bg-zinc-950 transition-colors duration-500">
        <div className="container mx-auto px-6">
          <h2 className="text-4xl md:text-5xl font-light mb-16 text-center tracking-wide" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 300 }}>
            {t.nav.exhibitions}
          </h2>
          <div className="max-w-5xl mx-auto">
            {events.length === 0 && (
              <p className="text-center text-sm font-light opacity-50" style={{ fontFamily: 'Raleway, sans-serif' }}>
                {language === 'es' ? 'No hay eventos disponibles.' : language === 'en' ? 'No events available.' : 'Aucun événement disponible.'}
              </p>
            )}
            {events.map((event) => (
              <div key={event.id} className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 mb-12">
                {/* Event Image - Las imágenes se buscan en /assets/eventos/ */}
                <div className="relative overflow-hidden bg-zinc-200 dark:bg-zinc-800 aspect-square">
                  <img
                    src={event.image}
                    alt={event.title[language]}
                    className="w-full h-full object-cover"
                  />
                </div>
                
                {/* Event Details */}
                <div className="flex flex-col justify-center space-y-6">
                  <h3 className="text-2xl md:text-3xl font-light tracking-wide leading-snug" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 400 }}>
                    {event.title[language]}
                  </h3>
                  
                  <p className="text-sm md:text-base font-light leading-relaxed opacity-70" style={{ fontFamily: 'Raleway, sans-serif', fontWeight: 300 }}>
                    {event.description[language]}
                  </p>
                  
                  {/* Dates */}
                  <div className="space-y-3">
                    {event.dates && event.dates.map((date, index) => (
                      <div key={index} className="flex items-start space-x-3">
                        <Calendar className="w-5 h-5 opacity-60 mt-0.5 flex-shrink-0" />
                        <span className="text-sm font-light opacity-70" style={{ fontFamily: 'Raleway, sans-serif', fontWeight: 300 }}>
                          {date[language]}
                        </span>
                      </div>
                    ))}
                  </div>
                  
                  {/* Venue */}
                  {event.venue && (
                    <div className="flex items-start space-x-3">
                      <MapPin className="w-5 h-5 opacity-60 mt-0.5 flex-shrink-0" />
                      <span className="text-sm font-light opacity-70" style={{ fontFamily: 'Raleway, sans-serif', fontWeight: 300 }}>
                        {event.venue[language]}
                      </span>
                    </div>
                  )}
                  
                  {/* Link */}
                  {event.link && (
                    <a
                      href={event.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center space-x-2 text-sm font-light opacity-70 hover:opacity-100 transition-opacity group"
                      style={{ fontFamily: 'Raleway, sans-serif', fontWeight: 300 }}
                    >
                      <span className="underline underline-offset-4">
                        {language === 'es' ? 'Más información y boletos' : language === 'en' ? 'More information and tickets' : "Plus d'informations et billets"}
                      </span>
                      <ExternalLink className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* <!-- FIN SECCIÓN EVENTOS --> */}

      {/* About Section */}
      <section id="about" className="py-24 bg-zinc-50 dark:bg-zinc-900 transition-colors duration-500">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-light mb-16 tracking-wide" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 300 }}>
              {t.about.title}
            </h2>
            <div className="space-y-8">
              <p className="text-base md:text-lg font-light leading-loose opacity-80" style={{ fontFamily: 'Raleway, sans-serif', fontWeight: 300 }}>
                {t.about.bio}
              </p>
              <p className="text-base md:text-lg font-light leading-loose opacity-80" style={{ fontFamily: 'Raleway, sans-serif', fontWeight: 300 }}>
                {t.about.bio2}
              </p>
              <p className="text-base md:text-lg font-light leading-loose opacity-80" style={{ fontFamily: 'Raleway, sans-serif', fontWeight: 300 }}>
                {t.about.bio3}
              </p>
              <p className="text-base md:text-lg font-light leading-loose opacity-80" style={{ fontFamily: 'Raleway, sans-serif', fontWeight: 300 }}>
                {t.about.bio4}
              </p>
              <p className="text-base md:text-lg font-light leading-loose opacity-80 italic" style={{ fontFamily: 'Raleway, sans-serif', fontWeight: 300 }}>
                {t.about.bio5}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-white dark:bg-zinc-950 transition-colors duration-500">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-light mb-16 tracking-wide" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 300 }}>
              {t.contact.title}
            </h2>
            <div className="space-y-8">
              {/* Email */}
              <a 
                href={`mailto:${t.contact.email}`}
                className="flex items-center justify-center space-x-4 group"
              >
                <Mail className="w-6 h-6 opacity-70 group-hover:opacity-100 transition-opacity" />
                <span className="text-lg md:text-xl font-light opacity-70 group-hover:opacity-100 transition-opacity" style={{ fontFamily: 'Raleway, sans-serif', fontWeight: 300 }}>
                  {t.contact.email}
                </span>
              </a>
              
              {/* Instagram */}
              <a 
                href={t.contact.instagramUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center space-x-4 group"
              >
                <Instagram className="w-6 h-6 opacity-70 group-hover:opacity-100 transition-opacity" />
                <span className="text-lg md:text-xl font-light opacity-70 group-hover:opacity-100 transition-opacity" style={{ fontFamily: 'Raleway, sans-serif', fontWeight: 300 }}>
                  {t.contact.instagram}
                </span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Lightbox */}
      {selectedGallery && (
        <GalleryLightbox
          images={selectedGallery}
          isOpen={lightboxOpen}
          onClose={closeLightbox}
          initialIndex={selectedImageIndex}
        />
      )}

      {/* Footer */}
      <footer className="py-12 border-t border-zinc-200 dark:border-zinc-800 transition-colors duration-500">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-sm font-light opacity-60">
              © {new Date().getFullYear()} Yael Martínez. {t.footer.rights}.
            </p>
            <div className="flex items-center space-x-6">
              <a href={t.contact.instagramUrl} target="_blank" rel="noopener noreferrer" className="text-sm font-light opacity-60 hover:opacity-100 transition-opacity">
                Instagram
              </a>
              <a href="https://www.magnumphotos.com/photographer/yael-martinez/" target="_blank" rel="noopener noreferrer" className="text-sm font-light opacity-60 hover:opacity-100 transition-opacity">
                Magnum Photos
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
